import cls from "@fvilers/cls";
import { CloseDetailsIcon } from "@imi/icons/CloseDetailsIcon.js";
import { OpenDetailsIcon } from "@imi/icons/OpenDetailsIcon.js";
import {
  ComponentProps,
  ComponentPropsWithRef,
  ElementType,
  ReactNode,
  useState,
} from "react";
import styles from "./Filters.module.css";

export type FiltersProps<T extends ElementType> = {
  as?: T;
} & ComponentPropsWithRef<T>;

export function Filters<T extends ElementType>({
  as,
  className,
  ...props
}: FiltersProps<T>) {
  const Comp = as || "div";
  return (
    <Comp
      className={cls(styles.Filters, className)}
      data-slot="actions"
      {...props}
    />
  );
}

export function FiltersCard({
  children,
  className,
  onToggle,
  open = false,
  summary,
  ...props
}: { summary: ReactNode } & ComponentProps<"details">) {
  const [isOpen, setIsOpen] = useState(open || false);
  const [prevOpen, setPrevOpen] = useState(open);

  if (open !== prevOpen) {
    setPrevOpen(open);
    setIsOpen(open || false);
  }

  return (
    <details
      className={cls(styles.FiltersCard, className)}
      onToggle={(e) => {
        setIsOpen(e.currentTarget.open);
        onToggle?.(e);
      }}
      open={isOpen}
      {...props}
    >
      <summary>
        {summary}
        {isOpen ? <CloseDetailsIcon /> : <OpenDetailsIcon />}
      </summary>
      {children}
    </details>
  );
}

export function FiltersCardContent({
  className,
  ...props
}: ComponentProps<"div">) {
  return (
    <div className={cls(styles.FiltersCardContent, className)} {...props} />
  );
}

export function FiltersFieldRow<T extends ElementType>({
  as,
  className,
  ...props
}: FiltersProps<T>) {
  const Comp = as || "div";
  return <Comp className={cls(styles.FiltersFieldRow, className)} {...props} />;
}
